import { useApiClient } from '@/hooks/useApiClient'
import { getCurrentMarket } from '@/utils/general'
import { useApi } from '@gravitate-js/the-armory'
import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import {
  type IdNameAddressResult,
  type IdNameResult,
  type LocationType,
  type MetadataResponse,
  configuredParams,
  sectorDepotMetadata,
  supplyOptionsMetadata,
} from './types'
import type { SectorDepotTreeData } from '@/components/_SiteWide/SectorDepotTreeSelect'

export const useMasterMetadata = <T>(paramConfigName: keyof typeof configuredParams) => {
  const api = useApi({ baseURLOverride: getAPIURL() })

  const getMetadataQuery = useQuery({
    queryKey: ['metadata', paramConfigName],
    queryFn: async () => {
      try {
        const response = await api.post('metadata', configuredParams[paramConfigName], {
          ignoreDefaults: true,
        })

        return response
      } catch (error) {
        console.error('Metadata fetch error:', error)
        throw error
      }
    },
  })

  return getMetadataQuery as UseQueryResult<T>
}

export function useLocations(type: LocationType) {
  const api = useApiClient()
  const market = getCurrentMarket()
  return api.useQuery('post', '/metadata', {
    body: {
      params: {
        results: {
          source: 'locations',
          format: 'id_name_address',
          type,
        },
      },
      market,
    },
  }) as UseQueryResult<
    {
      results: Array<IdNameAddressResult>
    },
    {
      detail?:
        | {
            loc: (string | number)[]
            msg: string
            type: string
          }[]
        | undefined
    }
  >
}

export const useScheduleMetadata = () =>
  useMasterMetadata<{
    tractors: Array<IdNameResult>
    trailers: Array<IdNameResult>
  }>('schedule')

export const useReasonCodes = () => useMasterMetadata<{ codes: Array<{ reason: string }> }>('reasonCodes')
export const useTractorMetadata = () => useMasterMetadata<{ tractors: Array<IdNameResult> }>('tractors')
export const useTrailerMetadata = () => useMasterMetadata<{ trailers: Array<IdNameResult> }>('trailers')
export function useSupplyOptionsMetadata(products: string[]) {
  const api = useApiClient()
  return api.useQuery('post', '/metadata', {
    body: {
      ...supplyOptionsMetadata(products),
    },
  }) as UseQueryResult<MetadataResponse>
}

export function useSectorDepotMetadata() {
  const api = useApiClient()
  const market = getCurrentMarket()
  return api.useQuery('post', '/metadata', {
    body: {
      ...sectorDepotMetadata,
      market,
    },
  }) as UseQueryResult<SectorDepotTreeData>
}
